// Coach-Governed Continuity Loop — "Worth carrying forward" panel.
// CGCL-05: persistence is fire-and-forget when coachId is provided.
// Actions update local state immediately (optimistic), then roll back on failure.
// Do NOT label actions as "Saved" or "Remembered".
// Coach-first rule: Never surface these outputs to leaders.
import { useState } from "react";
import { toast } from "sonner";
import type { CarryForwardCandidate, CarryForwardAction } from "@/types/carryForward";
import { CarryForwardCandidateCard } from "@/components/chat/CarryForwardCandidateCard";
import { supabase } from "@/integrations/supabase/client";
import { upsertContinuityReview } from "@/lib/behavior/coachContinuityReviewService";

interface WorthCarryingForwardPanelProps {
  candidates: CarryForwardCandidate[];
  coachId?: string;
  onActionSaved?: () => void;
  mode?: "leader" | "coach_practice";
}

export function WorthCarryingForwardPanel({ candidates, coachId, onActionSaved, mode = "leader" }: WorthCarryingForwardPanelProps) {
  const [actions, setActions] = useState<Record<string, CarryForwardAction>>({});

  if (candidates.length === 0) return null;

  function handleAction(candidateId: string, action: CarryForwardAction, reframedTitle?: string) {
    const previousAction = actions[candidateId];
    // Optimistic: update local state immediately regardless of DB result.
    setActions((prev) => ({ ...prev, [candidateId]: action }));

    if (coachId) {
      const candidate = candidates.find((c) => c.id === candidateId);
      if (candidate) {
        // Fire-and-forget: do not block UI on persistence result.
        upsertContinuityReview({
          supabase,
          coachId,
          candidate,
          action,
          reframedTitle,
        }).then(({ error }) => {
          if (error) {
            console.error("[WorthCarryingForwardPanel] persistence error:", error);
            setActions((prev) => {
              if (prev[candidateId] !== action) return prev;
              if (previousAction) {
                return { ...prev, [candidateId]: previousAction };
              }

              const next = { ...prev };
              delete next[candidateId];
              return next;
            });
            toast.error("Couldn't update this carry-forward item. Please try again.");
          } else {
            onActionSaved?.();
          }
        });
      }
    }
  }

  const count = candidates.length;
  const title =
    mode === "coach_practice"
      ? "Worth carrying forward in my practice"
      : "Worth carrying forward";
  const subtitle =
    mode === "coach_practice"
      ? count === 1
        ? "Veera surfaced a moment from your coaching practice that may be worth holding."
        : "Veera surfaced a few moments from your coaching practice that may be worth holding."
      : count === 1
        ? "Veera found an evidence-backed moment that may be useful next time. Review before making it active."
        : "Veera found a few evidence-backed moments that may be useful next time. Review before making anything active.";

  return (
    <div className="space-y-2 px-4 py-3 border-t border-border/70">
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {subtitle}
        </p>
      </div>
      <div className="space-y-2">
        {candidates.map((candidate) => (
          <CarryForwardCandidateCard
            key={candidate.id}
            candidate={candidate}
            actionTaken={actions[candidate.id]}
            onAction={handleAction}
            mode={mode}
          />
        ))}
      </div>
    </div>
  );
}
